import React from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { useModalScrollFix } from '../../hooks/useModalScrollFix';

/** Phone-only layout; one scroll owner and actions outside the keyboard's viewport. */
export function MobileFormPanel({ title, description, onClose, onRestoreFocus, footer, children }: {
  title: string; description: string; onClose: () => void; onRestoreFocus: () => void;
  footer: React.ReactNode; children: React.ReactNode;
}) {
  const titleId = React.useId();
  const scrollRef = React.useRef<HTMLDivElement>(null);
  useModalScrollFix(scrollRef);
  const [viewport, setViewport] = React.useState(() => ({ height: window.visualViewport?.height ?? window.innerHeight, top: window.visualViewport?.offsetTop ?? 0 }));
  React.useEffect(() => {
    const update = () => setViewport({ height: window.visualViewport?.height ?? window.innerHeight, top: window.visualViewport?.offsetTop ?? 0 });
    window.visualViewport?.addEventListener('resize', update);
    window.visualViewport?.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    return () => {
      window.visualViewport?.removeEventListener('resize', update);
      window.visualViewport?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);
  return <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
    <DialogContent onKeyDown={(event) => {
      if (event.key === 'Escape' && !event.defaultPrevented) { event.preventDefault(); onClose(); }
    }} aria-labelledby={titleId} className="!left-0 !w-full !max-w-none !max-h-none !translate-x-0 !translate-y-0 !rounded-none !p-0 !gap-0 !flex flex-col overflow-hidden" style={{ height: viewport.height, top: viewport.top }} closeButtonClassName="min-h-11 min-w-11 !top-[max(1rem,env(safe-area-inset-top))] !right-[max(1rem,env(safe-area-inset-right))]" onCloseAutoFocus={(event) => { event.preventDefault(); onRestoreFocus(); }}>
      <header className="shrink-0 border-b border-border px-4 pb-3 pr-16 pt-[max(1rem,env(safe-area-inset-top))]">
        <DialogTitle id={titleId} className="leading-snug">{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>
      </header>
      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto overscroll-contain pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))] py-4 space-y-5">{children}</div>
      <footer className="shrink-0 border-t border-border bg-background px-4 pt-3 pb-[max(.75rem,env(safe-area-inset-bottom))] flex gap-2 [&>button]:min-h-11 [&>button]:flex-1 [&>button]:whitespace-normal">{footer}</footer>
    </DialogContent>
  </Dialog>;
}
